import React, { useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useEa } from "@/components/energy-analytics/analytics/EaContext";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { EaKpiCard, EaNote, EaPageHeader, EaSeg, inputCls } from "@/components/energy-analytics/analytics/EaUi";
import { COLORS, fmtEur, fmtMwh, fmtQty, fmtSignedPct, gasLabel, monthsSpan } from "@/components/energy-analytics/analytics/eaFormat";
import { MIX_COLORS, SusPowerGas, SusRenewablePie, SusTrend } from "@/components/energy-analytics/analytics/EaSusCharts";
import { EaSusSettings } from "@/components/energy-analytics/analytics/EaSusSettings";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const tco2 = (v) => fmtQty(v, 1, "tCO₂");
const pp = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${fmtQty(v, 1)} p.p.`);
const span = (r, k) => (r[k].length ? `${monthsSpan(r[k])}${r[k].length < 12 ? " (anno parziale)" : ""}` : "nessun dato a consuntivo");

function MainKpis({ r }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <EaKpiCard accent={COLORS.power} label="Consumo Power" value={fmtMwh(r.power_mwh)} testid="sus-kpi-power"
        sub={`${r.points_power} POD · ${span(r, "months_power")}${r.self_mwh + r.ppa_onsite_mwh ? ` · rete ${fmtMwh(r.grid_mwh)} + autoconsumo ${fmtMwh(r.self_mwh + r.ppa_onsite_mwh)}` : ""}`} />
      <EaKpiCard accent={COLORS.gas} label="Consumo Gas" value={fmtQty(r.gas_smc, 0, "Smc")} testid="sus-kpi-gas"
        sub={`${r.points_gas} punti gas · ${span(r, "months_gas")}${r.biomethane_smc ? ` · di cui biometano ${fmtQty(r.biomethane_smc, 0, "Smc")}` : ""}`} />
      <EaKpiCard accent={COLORS.sustain} label="Energia elettrica rinnovabile" value={fmtQty(r.renewable_pct, 1, "%")} testid="sus-kpi-renewable"
        sub={r.renewable_pct == null ? "Dato non inserito" : `Non coperta ${fmtQty(100 - r.renewable_pct, 1, "%")} · ${fmtQty(r.renewable_mwh, 0, "MWh")} rinnovabili`} />
      <EaKpiCard accent="#0F766E" label="Emissioni energetiche totali stimate" value={tco2(r.co2_total_t)} sub={`Power ${tco2(r.co2_power_t)} · Gas ${tco2(r.co2_gas_t)}`} testid="sus-kpi-co2" />
      <EaKpiCard accent={COLORS.power} label="Emissioni Power stimate (Scope 2 location)" value={tco2(r.co2_power_t)} testid="sus-kpi-co2-power"
        sub={`Prelievi di rete × ${fmtQty(r.factors.power_kg_per_kwh, 4)} kg CO₂/kWh (${r.factors.source}) · market-based ${tco2(r.co2_power_market_t)} (residual mix ${fmtQty(r.factors.residual_kg_per_kwh, 4)})`} />
      <EaKpiCard accent={COLORS.gas} label="Emissioni Gas stimate (Scope 1)" value={tco2(r.co2_gas_t)} testid="sus-kpi-co2-gas"
        sub={`${fmtQty(r.factors.gas_kg_per_smc, 3)} kg CO₂/Smc (${r.factors.source})${r.co2_biogenic_t ? ` · CO₂ biogenica da biometano ${tco2(r.co2_biogenic_t)} (esclusa)` : ""}`} />
      <EaKpiCard accent={COLORS.sustain} label="Emissioni evitate" value={tco2(r.avoided_total_t)} testid="sus-kpi-avoided"
        sub="Rinnovabili elettriche × fattore location-based + biometano × fattore gas" />
    </div>
  );
}

function Mix({ r, cost }) {
  if (!r.mix.length && !r.biomethane_smc) return null;
  return (
    <div className="card-elevated overflow-x-auto" data-testid="sus-mix">
      <div className="p-4 font-display font-semibold text-slate-900" data-pdf-title>Fonti rinnovabili e strumenti contrattuali</div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{["Fonte", "Quantità", "% sul totale", "Emissioni evitate"].map((h, i) => <th key={h} className={`px-3 py-2 ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
        <tbody className="font-mono-nums">
          {r.mix.map((m) => (
            <tr key={m.key} className="border-t border-slate-100" data-testid={`sus-mix-${m.key}`}>
              <td className="px-3 py-2 font-sans"><span className="inline-block w-2.5 h-2.5 rounded-sm mr-2" style={{ background: MIX_COLORS[m.key] }} />{m.label}</td>
              <td className="px-3 py-2 text-right">{fmtQty(m.mwh, 0, "MWh")}</td><td className="px-3 py-2 text-right">{fmtQty(m.pct, 1, "%")}</td>
              <td className="px-3 py-2 text-right">{m.key === "grid" ? "—" : tco2(r.avoided_t[m.key])}</td>
            </tr>
          ))}
          {r.mix.length > 0 && (
            <tr className="border-t-2 border-slate-400 bg-slate-50 font-semibold text-slate-900" data-pdf-total data-testid="sus-mix-total">
              <td className="px-3 py-2 font-sans">Totale rinnovabile elettrico</td><td className="px-3 py-2 text-right">{fmtQty(r.renewable_mwh, 0, "MWh")}</td>
              <td className="px-3 py-2 text-right">{fmtQty(r.renewable_pct, 1, "%")}</td>
              <td className="px-3 py-2 text-right">{tco2(Object.entries(r.avoided_t).filter(([k]) => k !== "biomethane").reduce((a, [, v]) => a + v, 0))}</td>
            </tr>
          )}
          {r.biomethane_smc > 0 && (
            <tr className="border-t border-slate-100" data-testid="sus-mix-biomethane">
              <td className="px-3 py-2 font-sans"><span className="inline-block w-2.5 h-2.5 rounded-sm mr-2" style={{ background: COLORS.gas }} />Biometano certificato</td>
              <td className="px-3 py-2 text-right">{fmtQty(r.biomethane_smc, 0, "Smc")}</td><td className="px-3 py-2 text-right">{fmtQty(r.gas_smc ? (r.biomethane_smc / r.gas_smc) * 100 : null, 1, "% del gas")}</td>
              <td className="px-3 py-2 text-right">{tco2(r.avoided_t.biomethane)}</td>
            </tr>
          )}
        </tbody>
      </table>
      {cost && <div className="p-4 border-t border-slate-100 text-sm text-slate-700" data-testid="sus-avoided-cost">
        <b>Costo evitato stimato dall'autoconsumo:</b> {fmtEur(cost.avoided_eur)} — {fmtQty(cost.kwh / 1000, 0, "MWh")} autoconsumati valorizzati al PUN delle ore di irraggiamento solare ({fmtQty(cost.solar_pun, 1, "€/MWh")}, solo energia {fmtEur(cost.energy_eur)}){cost.other_eur_mwh != null ? ` + altre componenti (${fmtQty(cost.other_eur_mwh, 1, "€/MWh")}, ${cost.other_source})` : ""}{cost.ppa_cost_eur ? ` − costo PPA ${fmtEur(cost.ppa_cost_eur)} = risparmio netto ${fmtEur(cost.net_eur)}` : ""}.
      </div>}
    </div>
  );
}

function Yoy({ y }) {
  const sub = `${y.year} vs ${y.prev_year} · stessi mesi (${monthsSpan(y.months)})`;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="sus-yoy">
      <EaKpiCard band={false} accent={COLORS.power} label="Variazione consumi Power" value={fmtSignedPct(y.power_pct)} sub={sub} testid="sus-yoy-power" />
      <EaKpiCard band={false} accent={COLORS.gas} label="Variazione consumi Gas" value={fmtSignedPct(y.gas_pct)} sub={sub} testid="sus-yoy-gas" />
      <EaKpiCard band={false} accent="#0F766E" label="Variazione emissioni energetiche" value={fmtSignedPct(y.co2_pct)} sub={sub} testid="sus-yoy-co2" />
      <EaKpiCard band={false} accent={COLORS.sustain} label="Variazione % rinnovabile" value={pp(y.renewable_pp)} sub={`${y.year} vs ${y.prev_year} · anno intero`} testid="sus-yoy-renewable" />
    </div>
  );
}

function Intensity({ d }) {
  if (!d.intensity.length) return <EaNote testid="sus-intensity-empty">Per gli indicatori di intensità (kg CO₂/m², /tonnellata, /pezzo…) configura i driver di questo ambito nella sezione KPI → "Ambiti e driver".</EaNote>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="sus-intensity">
      {d.intensity.map((k) => (
        <EaKpiCard key={k.name} accent={COLORS.sustain} label={`kg CO₂ per ${k.unit} · ${k.name}`} value={fmtQty(k.co2_kg_per_unit, 2, `kg/${k.unit}`)} testid={`sus-intensity-${k.unit}`}
          sub={`${k.months.length ? monthsSpan(k.months) : "Driver non compilati"}${k.prev_co2_kg_per_unit ? ` · ${fmtSignedPct((k.co2_kg_per_unit / k.prev_co2_kg_per_unit - 1) * 100)} vs anno prec.` : ""}`} />
      ))}
    </div>
  );
}

function SummaryTable({ rows }) {
  const cols = ["Anno", "Power", "Gas", "% Rinnovabile", "CO₂ Power", "CO₂ Gas", "CO₂ Totale"];
  return (
    <div className="card-elevated overflow-x-auto" data-testid="sus-table">
      <div className="p-4 font-display font-semibold text-slate-900" data-pdf-title>Tabella sintetica</div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{cols.map((h, i) => <th key={h} className={`px-3 py-2 ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
        <tbody className="font-mono-nums">
          {[...rows].reverse().map((r) => (
            <tr key={r.year} className="border-t border-slate-100" data-testid={`sus-row-${r.year}`}>
              <td className="px-3 py-2 font-sans font-semibold">{r.year}{r.months_power.length < 12 && r.months_power.length > 0 ? "*" : ""}</td>
              <td className="px-3 py-2 text-right">{fmtMwh(r.power_mwh)}</td><td className="px-3 py-2 text-right">{fmtQty(r.gas_smc, 0, "Smc")}</td>
              <td className="px-3 py-2 text-right">{fmtQty(r.renewable_pct, 1, "%")}</td><td className="px-3 py-2 text-right">{tco2(r.co2_power_t)}</td>
              <td className="px-3 py-2 text-right">{tco2(r.co2_gas_t)}</td><td className="px-3 py-2 text-right font-semibold">{tco2(r.co2_total_t)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Analysis({ d }) {
  const r = d.selected;
  if (!r) return <EaNote>Nessun dato per l'anno selezionato.</EaNote>;
  return (
    <div className="space-y-6">
      <MainKpis r={r} />
      {d.yoy && <Yoy y={d.yoy} />}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4"><SusRenewablePie row={r} /><SusPowerGas row={r} /></div>
      <Mix r={r} cost={d.avoided_cost} />
      <SusTrend rows={d.rows} />
      <Intensity d={d} />
      <SummaryTable rows={d.rows} />
      <EaNote>Stima delle emissioni = consumi a consuntivo × fattore emissivo dell'anno (Power in kg CO₂/kWh, Gas in kg CO₂/Smc). Power = prelievi di rete × mix medio nazionale ISPRA (Scope 2 location-based); l'autoconsumo da autoproduzione e PPA on-site ha emissioni zero; il market-based valorizza la rete non coperta da GO/PPA off-site con il residual mix AIB. Gas = solo quota fossile (Scope 1); il biometano è riportato come CO₂ biogenica. * = anno parziale.</EaNote>
    </div>
  );
}

function ScopePicker({ scope, setScope, ref_, setRef, sites, points }) {
  const opts = [["company", "Totale aziendale"], ...(sites.length ? [["site", "Sito"]] : []), ["point", "POD/PDR"]];
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <EaSeg value={scope} onChange={(v) => { setScope(v); setRef(""); }} options={opts} testid="sus-scope" />
      {scope === "site" && <select value={ref_} onChange={(e) => setRef(e.target.value)} className={inputCls} data-testid="sus-scope-site">{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>}
      {scope === "point" && <select value={ref_} onChange={(e) => setRef(e.target.value)} className={`${inputCls} max-w-[340px]`} data-testid="sus-scope-point">{points.map((p) => <option key={p.code} value={p.code}>{p.commodity === "GAS" ? gasLabel(p) : `POD ${p.identifier}`}{p.label ? ` · ${p.label}` : ""}</option>)}</select>}
    </div>
  );
}

function EsgButton({ companyId, year }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/ea/sustainability/esg-report.pdf", { params: { year, company_id: companyId || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const name = /filename="([^"]+)"/.exec(r.headers?.["content-disposition"] || "")?.[1] || `Report_ESG_${year}.pdf`;
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Report ESG non disponibile"); }
    setBusy(false);
  };
  return (
    <button onClick={download} disabled={busy} className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="sus-esg-report-btn">
      {busy ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} Report ESG {year}
    </button>
  );
}

export default function EaSustainability() {
  const { nav, filters } = useEa();
  const [scope, setScope] = useState("company");
  const [ref, setRef] = useState("");
  const [v, setV] = useState(0);
  const sitesRes = useEaData("/ea/efficiency/sites");
  const sites = (sitesRes.data?.sites || []).filter((s) => s.scope === "site");
  const points = nav?.points || [];
  const effRef = scope === "site" ? ref || sites[0]?.id : scope === "point" ? ref || points[0]?.code : undefined;
  const { data: d, loading, error } = useEaData("/ea/sustainability", { scope, ref: effRef, v });
  return (
    <div className="space-y-6" data-testid="ea-sustainability">
      <EaPageHeader title="Sostenibilità" subtitle={`Consumi, quota di energia elettrica rinnovabile ed emissioni energetiche stimate${d?.label ? ` · ${d.label}` : ""}${d?.year ? ` · anno ${d.year}` : ""}.`}
        right={d?.year && <EsgButton companyId={filters.company} year={d.year} />} />
      <Tabs defaultValue="analysis">
        <TabsList data-testid="sus-tabs"><TabsTrigger value="analysis" data-testid="sus-tab-analysis">Analisi</TabsTrigger><TabsTrigger value="settings" data-testid="sus-tab-settings">Dati e fattori</TabsTrigger></TabsList>
        <TabsContent value="analysis" className="mt-4 space-y-6">
          <ScopePicker scope={scope} setScope={setScope} ref_={effRef || ""} setRef={setRef} sites={sites} points={points} />
          {loading && !d ? <div className="text-sm text-slate-400" data-testid="ea-loading">Caricamento…</div> : error ? <EaNote>{error}</EaNote> : d && <Analysis d={d} />}
        </TabsContent>
        <TabsContent value="settings" className="mt-4">
          <EaSusSettings companyId={filters.company} years={nav?.years || []} year={Number(filters.year) || nav?.year} points={points} onSaved={() => setV((x) => x + 1)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
